import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrosoftAuth } from '../context/MicrosoftAuthContext';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import SearchBar from '../components/SearchBar';
import ReminderTable from '../components/features/reminders/ReminderTable';
import CancelConfirmModal from '../components/CancelConfirmModal';
import { getReminderRecipients, cancelReminderRecipient, cancelAllReminderRecipients } from '../api/reminderService';
import AccessDeniedView from '../components/features/logs/AccessDeniedView';
import SubmissionPageHeader from '../components/features/logs/SubmissionPageHeader';

const ManageReminders = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { userTeamData } = useMicrosoftAuth();
    const { isDark } = useTheme();

    const [reminders, setReminders] = useState([]);
    const [stats, setStats] = useState(null);
    const [submission, setSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [accessDenied, setAccessDenied] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelModalData, setCancelModalData] = useState({ recipientId: null, isCancelAll: false });
    const [isCancelling, setIsCancelling] = useState(false);

    const fetchReminderRecipients = async () => {
        try {
            setLoading(true);

            const groups = userTeamData?.user_group || [];
            const branches = userTeamData?.branch_manager_branches
                ? userTeamData.branch_manager_branches.map(b => b.branch_id)
                : [];

            const response = await getReminderRecipients(id, userTeamData, groups, branches);

            if (response.status) {
                setReminders(response.data);
                setStats(response.stats);
                setSubmission(response.submission);
            } else {
                toast.error(response.message || 'Failed to fetch reminder recipients');
            }
        } catch (error) {
            console.error('Error fetching reminder recipients:', error);
            if (error.response?.status === 401) {
                toast.error('Your session has expired. Please login again.');
                navigate('/login');
            } else if (error.response?.status === 404) {
                setAccessDenied(error.response?.data?.message || 'Submission not found or access denied');
            } else {
                toast.error(error.response?.data?.message || 'Failed to fetch reminder recipients');
            }
        } finally {
            setLoading(false);
        }
    };

    const canCancelReminders = () => {
        if (!submission || !userTeamData) return false;

        if (userTeamData?.user_role_id?.includes("11")) {
            return true;
        }

        if (userTeamData?.user_role_id?.includes("10")) {
            return submission.user_team_id === userTeamData.id;
        }

        const groups = userTeamData?.user_group || [];
        const branches = userTeamData?.branch_manager_branches?.map(b => String(b.branch_id)) || [];

        return (
            submission.email === userTeamData.email ||
            groups.includes(submission.user_team_id) ||
            (submission.branch_id != null && branches.includes(String(submission.branch_id)))
        );
    };

    const handleCancelReminder = (recipientId) => {
        if (!canCancelReminders()) {
            toast.error('You do not have permission to cancel reminders for this submission');
            return;
        }
        setCancelModalData({ recipientId, isCancelAll: false });
        setShowCancelModal(true);
    };

    const handleCancelAll = () => {
        if (!canCancelReminders()) {
            toast.error('You do not have permission to cancel reminders for this submission');
            return;
        }
        setCancelModalData({ recipientId: null, isCancelAll: true });
        setShowCancelModal(true);
    };

    const confirmCancel = async () => {
        setIsCancelling(true);
        try {
            const groups = userTeamData?.user_group || [];
            const branches = userTeamData?.branch_manager_branches
                ? userTeamData.branch_manager_branches.map(b => b.branch_id)
                : [];

            if (cancelModalData.isCancelAll) {
                const response = await cancelAllReminderRecipients(id, userTeamData, groups, branches);
                if (response.status) {
                    toast.success(response.message || 'All queued reminders cancelled successfully');
                    fetchReminderRecipients();
                } else {
                    toast.error(response.message || 'Failed to cancel all reminders');
                }
            } else {
                const response = await cancelReminderRecipient(cancelModalData.recipientId, userTeamData, groups, branches);
                if (response.status) {
                    toast.success('Reminder cancelled successfully');
                    fetchReminderRecipients();
                } else {
                    toast.error(response.message || 'Failed to cancel reminder');
                }
            }
            setShowCancelModal(false);
            setCancelModalData({ recipientId: null, isCancelAll: false });
        } catch (error) {
            console.error('Error cancelling reminder:', error);
            toast.error(error.response?.data?.message || 'Failed to cancel reminder');
        } finally {
            setIsCancelling(false);
        }
    };

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (userTeamData) {
            fetchReminderRecipients();
        }
    }, [id, userTeamData]);

    // Filter reminders based on search query
    const filteredReminders = searchQuery && searchQuery.trim()
        ? reminders.filter(reminder => {
            const query = searchQuery.toLowerCase();
            return (
                reminder.recipient_type?.toLowerCase().includes(query) ||
                reminder.status?.toLowerCase().includes(query) ||
                reminder.planNumber?.toLowerCase().includes(query) ||
                reminder.lotNumber?.toLowerCase().includes(query) ||
                reminder.unitNumber?.toLowerCase().includes(query) ||
                reminder.name?.toLowerCase().includes(query) ||
                reminder.email?.toLowerCase().includes(query) ||
                reminder.mobilePhone?.toLowerCase().includes(query) ||
                reminder.type?.toLowerCase().includes(query)
            );
        })
        : reminders;

    if (accessDenied) {
        return <AccessDeniedView isDark={isDark} accessDenied={accessDenied} />;
    }

    return (
        <>
            <CancelConfirmModal
                show={showCancelModal}
                onHide={() => {
                    setShowCancelModal(false);
                    setCancelModalData({ recipientId: null, isCancelAll: false });
                }}
                onConfirm={confirmCancel}
                message={
                    cancelModalData.isCancelAll
                        ? (reminders.length === 0
                            ? 'Are you sure you want to cancel all reminders for this submission?'
                            : `Are you sure you want to cancel all ${stats?.queued || 0} queued reminders?`)
                        : 'Are you sure you want to cancel this reminder?'
                }
                isLoading={isCancelling}
                isDark={isDark}
            />
        <div className={`min-h-screen px-[16px] sm:px-[24px] py-[20px] sm:py-[30px] flex flex-col gap-[20px] sm:gap-[30px] transition-colors duration-200 ${
            isDark ? 'bg-[#000000]' : 'bg-[#f5f5f5]'
        }`}>
            {/* Header */}
            <SubmissionPageHeader id={id} isDark={isDark} title="Manage Reminders" description={`Manage reminder recipients for Submission #${id}`} />

            {/* Filters card */}
            <div className={`border-solid border-[1px] rounded-[16px] sm:rounded-[24px] p-[16px] sm:p-[24px] flex flex-col gap-[12px] sm:gap-[16px] transition-colors duration-200 ${
                isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
            }`}>
                <div className='flex items-center gap-[10px]'>
                    <span className={`text-[14px] sm:text-[16px] font-semibold transition-colors duration-200 ${
                        isDark ? 'text-white' : 'text-[#072837]'
                    }`}>Filters:</span>
                </div>
                <div className='flex items-center h-[40px]'>
                    <div className='w-full sm:w-[471px]'>
                        <SearchBar
                            value={searchQuery}
                            placeholder="Search for Recipients"
                            onSearch={setSearchQuery}
                            disabled={loading}
                            variant="logs"
                            isDark={isDark}
                        />
                    </div>
                </div>
            </div>

            {/* Reminders Table */}
            <ReminderTable
                data={filteredReminders}
                title={`Reminder Recipients (${filteredReminders.length})`}
                loading={loading}
                isDark={isDark}
                onCancelReminder={handleCancelReminder}
                showCancelAllButton={(stats?.queued > 0 || (submission?.reminderStatus === '1' && reminders.length === 0)) && canCancelReminders()}
                onCancelAll={handleCancelAll}
                canCancel={canCancelReminders()}
                emptyMessage={
                    submission?.reminderStatus === '1' && reminders.length === 0
                        ? 'No reminder recipients have been created yet'
                        : 'No reminder recipients found'
                }
            />
        </div>
        </>
    );
};

export default ManageReminders;
